import { type FormEvent, useEffect, useState } from 'react';
import { Button, Group, Input, LoadingOverlay, Modal, Stack, TextInput } from '@mantine/core';
import { IMaskInput } from 'react-imask';
import type { SuperAdminCreateRequest, SuperAdminUser } from '@/types/users';
import { PHONE_NUMBER_MASK } from '@/constants';
import { formatPhoneNumber, isPhoneValueEmpty, toServerPhoneNumber } from '@/helpers';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';
import modalStyles from '@/components/EditFormModal/EditFormModal.module.sass';

type SuperAdminModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (data: Partial<SuperAdminCreateRequest>) => Promise<void> | void;
  user?: SuperAdminUser | null;
};

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

const INITIAL_DATA: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

export const SuperAdminModal = ({ opened, onClose, onSave, user }: SuperAdminModalProps) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  useEffect(() => {
    if (user) {
      let phoneNumber = '';
      if (user.phoneNumber) {
        const server = toServerPhoneNumber(user.phoneNumber);
        phoneNumber = isPhoneValueEmpty(server) ? '' : server;
      }

      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber,
      });
    } else {
      setFormData(INITIAL_DATA);
    }
    setEmailError(null);
  }, [user, opened]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') setEmailError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!user && !formData.email.endsWith('@diocesan.com')) {
      setEmailError('Email must be a @diocesan.com address');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const phoneNumberE164 = isPhoneValueEmpty(formData.phoneNumber) ? null : formData.phoneNumber.trim();

      const payload: Partial<SuperAdminCreateRequest> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: phoneNumberE164,
      };

      if (!user) {
        payload.email = formData.email;
      }

      await onSave(payload);
      setFormData(INITIAL_DATA);
      onClose();
    } catch (error) {
      console.error('Failed to save super admin', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(INITIAL_DATA);
    onClose();
  };

  const isValid = formData.firstName && formData.lastName && formData.email;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={user ? 'Edit Super Admin' : 'New Super Admin'}
      centered
      classNames={{
        header: modalStyles.modalHeader,
        title: modalStyles.modalTitle,
        close: modalStyles.closeButton,
      }}
    >
      <LoadingOverlay visible={isSaving} />
      <form onSubmit={handleSubmit}>
        <Stack gap="md" mt="md">
          <TextInput
            label="First Name"
            placeholder="John"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            required
            data-autofocus
          />
          <TextInput
            label="Last Name"
            placeholder="Doe"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            required
          />
          <TextInput
            label="Email"
            placeholder="johndoe@diocesan.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            type="email"
            disabled={!!user}
            error={emailError}
          />
          <Input.Wrapper label="Phone">
            <Input
              component={IMaskInput}
              mask={PHONE_NUMBER_MASK}
              placeholder={PHONE_NUMBER_MASK}
              value={formatPhoneNumber(formData.phoneNumber)}
              unmask={false}
              onAccept={(value: string) => {
                const server = toServerPhoneNumber(value);
                setFormData((prev) => ({ ...prev, phoneNumber: isPhoneValueEmpty(server) ? '' : server }));
              }}
            />
          </Input.Wrapper>
          <SaveErrorBox error={saveError} />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSaving} disabled={!isValid}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
